import React, { useState, useRef } from 'react';
import { UserPlus, Download, Info, Upload, Check } from 'lucide-react';
import { toast } from 'react-toastify';
import useUsersStore from '@/stores/AdminStore/UsersStore';
import './AddImportUsersTab.css';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  role: 'student' | 'instructor' | 'admin';
  department: string;
  jobTitle: string;
}

const AddImportUsersTab: React.FC = () => {
  const { departments, creating, importing, createUser, bulkImportUsers } = useUsersStore();

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    role: 'student',
    department: '',
    jobTitle: '',
  });

  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.firstName || !formData.lastName || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    const result = await createUser(formData);

    if (result.success) {
      toast.success(result.message);
      if (result.data?.generatedPassword) {
        setGeneratedPassword(result.data.generatedPassword);
      }
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        role: 'student',
        department: '',
        jobTitle: '',
      });
    } else {
      toast.error(result.message);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setCsvFile(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        toast.error('Please drop a CSV file');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setCsvFile(file);
    }
  };

  const handleImportUsers = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file first');
      return;
    }

    try {
      const text = await csvFile.text();
      const lines = text.split('\n').filter((line) => line.trim());

      if (lines.length < 2) {
        toast.error('CSV file must have a header row and at least one data row');
        return;
      }

      // Parse header
      const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
      const requiredHeaders = ['firstname', 'lastname', 'email'];
      const missingHeaders = requiredHeaders.filter((h) => !headers.includes(h));

      if (missingHeaders.length > 0) {
        toast.error(`Missing required columns: ${missingHeaders.join(', ')}`);
        return;
      }

      // Parse data rows
      const users = lines.slice(1).map((line) => {
        const values = line.split(',').map((v) => v.trim());
        const user: any = {};

        headers.forEach((header, index) => {
          const value = values[index] || '';
          switch (header) {
            case 'firstname':
              user.firstName = value;
              break;
            case 'lastname':
              user.lastName = value;
              break;
            case 'email':
              user.email = value;
              break;
            case 'role':
              user.role = value || 'student';
              break;
            case 'department':
              user.department = value;
              break;
            case 'jobtitle':
              user.jobTitle = value;
              break;
          }
        });

        return user;
      });

      const result = await bulkImportUsers(users);

      if (result.success) {
        toast.success(result.message);
        setCsvFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('Failed to parse CSV file');
    }
  };

  const downloadTemplate = () => {
    const headers = 'firstName,lastName,email,role,department,jobTitle';
    const example = 'John,Doe,john.doe@company.com,student,Engineering,Software Engineer';
    const csv = `${headers}\n${example}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'user_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="admin-user-import-container">
      <div className="admin-user-import-card">
        <div className="admin-user-import-card-header">
          <div className="admin-user-import-card-icon orange">
            <UserPlus size={28} />
          </div>
          <div className="admin-user-import-card-title">
            <h2>Add Single User</h2>
            <p>Create a new user account manually</p>
          </div>
        </div>

        <form onSubmit={handleCreateUser}>
          <div className="admin-user-import-form-row">
            <div className="admin-user-import-form-group">
              <label className="admin-user-import-form-label">First Name *</label>
              <input
                type="text"
                name="firstName"
                className="admin-user-import-form-input"
                placeholder="John"
                value={formData.firstName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="admin-user-import-form-group">
              <label className="admin-user-import-form-label">Last Name *</label>
              <input
                type="text"
                name="lastName"
                className="admin-user-import-form-input"
                placeholder="Doe"
                value={formData.lastName}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div className="admin-user-import-form-group">
            <label className="admin-user-import-form-label">Email *</label>
            <input
              type="email"
              name="email"
              className="admin-user-import-form-input"
              placeholder="john.doe@company.com"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="admin-user-import-form-row">
            <div className="admin-user-import-form-group">
              <label className="admin-user-import-form-label">Role</label>
              <select
                name="role"
                className="admin-user-import-form-input"
                value={formData.role}
                onChange={handleInputChange}
              >
                <option value="student">Student</option>
                <option value="instructor">Instructor</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="admin-user-import-form-group">
              <label className="admin-user-import-form-label">Department</label>
              <select
                name="department"
                className="admin-user-import-form-input"
                value={formData.department}
                onChange={handleInputChange}
              >
                <option value="">Select</option>
                {departments.map((dept) => (
                  <option key={dept} value={dept}>
                    {dept}
                  </option>
                ))}
                <option value="Engineering">Engineering</option>
                <option value="Sales">Sales</option>
                <option value="HR">HR</option>
                <option value="Design">Design</option>
              </select>
            </div>
          </div>

          <div className="admin-user-import-form-group">
            <label className="admin-user-import-form-label">Job Title</label>
            <input
              type="text"
              name="jobTitle"
              className="admin-user-import-form-input"
              placeholder="Software Engineer"
              value={formData.jobTitle}
              onChange={handleInputChange}
            />
          </div>

          <button type="submit" className="admin-user-import-submit-btn" disabled={creating}>
            <UserPlus size={18} />
            {creating ? 'Creating...' : 'Create User'}
          </button>
        </form>

        {generatedPassword && (
          <div className="admin-user-import-password-display">
            <Check size={18} />
            <span>User created! Temporary password: <strong>{generatedPassword}</strong></span>
            <button onClick={() => {
              navigator.clipboard.writeText(generatedPassword);
              toast.success('Password copied to clipboard');
            }}>
              Copy
            </button>
          </div>
        )}
      </div>

      <div className="admin-user-import-card">
        <div className="admin-user-import-card-header">
          <div className="admin-user-import-card-icon blue">
            <Upload size={28} />
          </div>
          <div className="admin-user-import-card-title">
            <h2>Bulk Import</h2>
            <p>Import multiple users from CSV file</p>
          </div>
        </div>

        <div className="admin-user-import-info-box">
          <div className="admin-user-import-info-title">
            <Info size={18} />
            CSV Format Requirements
          </div>
          <ul className="admin-user-import-info-list">
            <li>Required: firstName, lastName, email</li>
            <li>Optional: role, department, jobTitle</li>
            <li>Max file size: 10MB</li>
          </ul>
        </div>

        <button className="admin-user-import-download-btn" onClick={downloadTemplate}>
          <Download size={18} />
          Download CSV Template
        </button>

        <div
          className={`admin-user-import-upload-area ${csvFile ? 'has-file' : ''}`}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          {csvFile ? (
            <>
              <Check size={48} className="admin-user-import-upload-success" />
              <div className="admin-user-import-upload-text">{csvFile.name}</div>
              <div className="admin-user-import-upload-subtext">
                Click to change file
              </div>
            </>
          ) : (
            <>
              <div className="admin-user-import-upload-icon">
                <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
                  <path
                    d="M28 14V35M28 14L21 21M28 14L35 21"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M14 35V42C14 43.1046 14.8954 44 16 44H40C41.1046 44 42 43.1046 42 42V35"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <div className="admin-user-import-upload-text">
                Drop CSV file here or click to browse
              </div>
              <div className="admin-user-import-upload-subtext">Supports .csv files up to 10MB</div>
            </>
          )}
        </div>

        <button
          className="admin-user-import-submit-btn"
          onClick={handleImportUsers}
          disabled={importing || !csvFile}
        >
          <Upload size={18} />
          {importing ? 'Importing...' : 'Import Users'}
        </button>
      </div>
    </div>
  );
};

export default AddImportUsersTab;
